import { Component } from '@theme/component';
import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;
  static shippingUnlocked;
  static shippingPending = false;
  #shippingFrame = 0;
  #shippingObserver = new MutationObserver(() => this.#checkShipping());
  #confetti = null;

  connectedCallback() {
    super.connectedCallback();
    CartDrawerComponent.shippingUnlocked ??= Number(this.dataset.initialTotal) >= Number(this.dataset.shippingThreshold);
    this.#shippingObserver.observe(this, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-shipping-total', 'open'] });
    this.#checkShipping();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#shippingObserver.disconnect();
    cancelAnimationFrame(this.#shippingFrame);
    this.#confetti?.remove();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #checkShipping() {
    const bar = this.querySelector('lcp-shipping-progress');
    if (!bar) {
      if (this.querySelector('.cart-drawer__heading--empty')) {
        CartDrawerComponent.shippingUnlocked = false;
        CartDrawerComponent.shippingPending = false;
      }
      return;
    }
    const unlocked = Number(bar.dataset.shippingTotal) >= Number(bar.dataset.shippingThreshold);
    if (unlocked && !CartDrawerComponent.shippingUnlocked) CartDrawerComponent.shippingPending = true;
    if (!unlocked) CartDrawerComponent.shippingPending = false;
    CartDrawerComponent.shippingUnlocked = unlocked;
    if (!CartDrawerComponent.shippingPending || !this.refs.dialog?.open) return;
    CartDrawerComponent.shippingPending = false;
    cancelAnimationFrame(this.#shippingFrame);
    this.#shippingFrame = requestAnimationFrame(() => {
      this.#shippingFrame = requestAnimationFrame(() => this.#celebrateShipping());
    });
  }

  #celebrateShipping() {
    const dialog = this.refs.dialog;
    const bar = this.querySelector('lcp-shipping-progress');
    if (!dialog?.open || !bar || !CartDrawerComponent.shippingUnlocked || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.#confetti?.remove();
    const layer = document.createElement('div');
    layer.className = 'lcp-shipping-confetti';
    layer.setAttribute('aria-hidden', 'true');
    const bounds = dialog.getBoundingClientRect();
    const origin = bar.getBoundingClientRect();
    const y = Math.min(bounds.height - 120, Math.max(140, origin.top - bounds.top + 15));
    const colors = ['#602b35', '#bd8b95', '#d8bd8b', '#f5f1ec'];
    this.#confetti = layer;
    dialog.append(layer);
    const animations = [];
    for (let i = 0; i < 54; i++) {
      const piece = document.createElement('i');
      piece.className = 'lcp-shipping-confetti__piece';
      piece.style.left = bounds.width / 2 + 'px';
      piece.style.top = y + 'px';
      piece.style.backgroundColor = colors[i % colors.length];
      layer.append(piece);
      const x = (Math.random() - 0.5) * bounds.width * 1.6;
      const rise = 100 + Math.random() * 200;
      const turn = (Math.random() - 0.5) * 1000;
      const animation = piece.animate([
        { transform: 'translate(0,0) rotate(0deg)', opacity: 1 },
        { transform: `translate(${x * .65}px,${-rise}px) rotate(${turn * .5}deg)`, opacity: 1, offset: .4 },
        { transform: `translate(${x}px,140px) rotate(${turn}deg)`, opacity: 0 }
      ], { duration: 1700 + Math.random() * 400, easing: 'cubic-bezier(.2,.6,.4,1)' });
      animations.push(animation.finished.catch(() => {}));
    }
    Promise.all(animations).then(() => {
      layer.remove();
      if (this.#confetti === layer) this.#confetti = null;
    });
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

class LcpCartPairing extends Component {
  #controller;
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.addEventListener('submit', this.#add, { signal: this.#controller.signal });
    this.addEventListener('change', this.#select, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  #select = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const card = select.closest('.lcp-pairing__card');
    const option = select.selectedOptions[0];
    if (!card || !option?.value) return;
    card.querySelector('.lcp-pairing__price').textContent = option.dataset.price;
    card.querySelector('[name="quantity"]').value = option.dataset.quantity || '1';
  };

  #add = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();
    if (this.#busy || !form.reportValidity()) return;
    const button = form.querySelector('button[type="submit"]');
    const status = this.querySelector('[role="status"]');
    if (!button || !status) return;
    const originalLabel = button.textContent;
    this.#busy = true;
    for (const control of this.querySelectorAll('button[type="submit"]')) control.disabled = true;
    button.textContent = this.dataset.adding;
    status.textContent = '';
    const formData = new FormData(form);
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(node => node.dataset.sectionId))].slice(0, 5);
    formData.set('sections', sectionIds.join(','));
    formData.set('sections_url', window.location.pathname);
    try {
      const response = await fetch(form.action, { method: 'POST', body: formData, headers: { Accept: 'application/json' }, signal: this.#controller.signal });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.error);
      status.textContent = this.dataset.added;
      const cartItems = this.closest('cart-items-component');
      const sectionId = cartItems?.dataset.sectionId;
      const sectionHTML = sectionId && result.sections?.[sectionId];
      if (!cartItems || !sectionId || !sectionHTML) {
        window.location.assign(Theme.routes.cart_url);
        return;
      }
      const parsedSection = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const itemCount = Number(parsedSection.querySelector('[ref="cartItemCount"]')?.textContent) || 0;
      cartItems.dispatchEvent(new CartUpdateEvent({}, sectionId, {
        source: 'cart-items-component', itemCount, sections: result.sections
      }));
      await morphSection(sectionId, sectionHTML, 'hydration');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (document.activeElement === document.body || document.activeElement?.tagName === 'DIALOG') {
          const heading = document.querySelector('#cart-drawer-heading');
          heading?.setAttribute('tabindex', '-1');
          heading?.focus({ preventScroll: true });
        }
      }));
    } catch (error) {
      if (error.name !== 'AbortError') status.textContent = error.message || this.dataset.error;
    } finally {
      this.#busy = false;
      button.textContent = originalLabel;
      for (const control of this.querySelectorAll('button[type="submit"]')) control.disabled = false;
    }
  };
}

if (!customElements.get('lcp-cart-pairing')) {
  customElements.define('lcp-cart-pairing', LcpCartPairing);
}
